// src/providers/SupabaseProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  createClient,
  type SupabaseClient,
  type Session,
} from "@supabase/supabase-js";
import { User } from "../models/User";
import { BackendService } from "../services/backendService";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface SupabaseContextType {
  supabase: SupabaseClient;
  session: Session | null;
  user: User | null;
  signInWithEmail: (email: string, password?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  loading: boolean;
  updateTokens: (newTokens: number) => Promise<void>;
  /** Creates (or resumes) an anonymous session so the user can "Dive In" without credentials */
  signInAnonymously: () => Promise<void>;
  /** Convert an anonymous account into a permanent email+password account */
  linkAnonymousWithEmailPassword: (
    email: string,
    password: string,
  ) => Promise<"complete" | "verify_email">;
}

/* ------------------------------------------------------------------ */
/*  Environment vars (CRA prefix = REACT_APP_)                        */
/* ------------------------------------------------------------------ */
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL as string | undefined;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY as
  | string
  | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase env vars. Define REACT_APP_SUPABASE_URL and " +
      "REACT_APP_SUPABASE_ANON_KEY in a .env file at the project root.",
  );
}

/* ------------------------------------------------------------------ */
/*  Supabase client & context                                         */
/* ------------------------------------------------------------------ */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

const SupabaseContext = createContext<SupabaseContextType | undefined>(
  undefined,
);

/* ------------------------------------------------------------------ */
/*  Provider                                                          */
/* ------------------------------------------------------------------ */
export const SupabaseProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendUserCreated, setBackendUserCreated] = useState<Set<string>>(
    new Set(),
  );

  /* -------------------------------------------------------------- */
  /*  Once we have a signed-in user, notify backend (idempotent)    */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    if (!user?.id) return;

    if (backendUserCreated.has(user.id)) {
      console.log(`Backend user already created for ${user.id}`);
      return;
    }

    // Retrieve name collected on first sign-in (if any)
    const pendingName = localStorage.getItem("pendingUsername");
    console.log(
      `localStorage.getItem("pendingUsername") returned: "${pendingName}"`,
    );
    console.log(`All localStorage keys:`, Object.keys(localStorage));
    const usernameCandidate = pendingName ?? user.email ?? "";

    if (!usernameCandidate) {
      console.log(
        `Username not available yet for ${user.id} – skipping backend createUser`,
      );
      return;
    }

    console.log(
      `Creating backend user for ${user.id} with name: ${usernameCandidate}`,
    );

    BackendService.getInstance()
      .createUser(user.id, usernameCandidate)
      .then(() => {
        console.log(`Successfully created backend user for ${user.id}`);
        setBackendUserCreated((prev) => new Set(prev).add(user.id));
        // Clear pendingUsername after successful backend creation
        if (pendingName) {
          localStorage.removeItem("pendingUsername");
          console.log(`Cleared pendingUsername: ${pendingName}`);
        }
      })
      .catch((err) => {
        console.error("Failed to notify backend of new user:", err);
      });
  }, [user?.id, backendUserCreated]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const supabaseUser = session?.user;
      if (supabaseUser) {
        setUser({
          id: supabaseUser.id,
          email: supabaseUser.email || "",
          adultMode:
            (supabaseUser.user_metadata as { adultMode?: boolean })
              ?.adultMode ?? true,
          tokens:
            (supabaseUser.user_metadata as { tokens?: number })?.tokens ?? 0,
          // Supabase marks anonymous users by having the provider set to 'anonymous'
          isAnonymous:
            (supabaseUser as unknown as { is_anonymous?: boolean })
              ?.is_anonymous ?? supabaseUser.email === null,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      const supabaseUser = session?.user;
      if (supabaseUser) {
        setUser({
          id: supabaseUser.id,
          email: supabaseUser.email || "",
          adultMode:
            (supabaseUser.user_metadata as { adultMode?: boolean })
              ?.adultMode ?? true,
          tokens:
            (supabaseUser.user_metadata as { tokens?: number })?.tokens ?? 0,
          // Supabase marks anonymous users by having the provider set to 'anonymous'
          isAnonymous:
            (supabaseUser as unknown as { is_anonymous?: boolean })
              ?.is_anonymous ?? supabaseUser.email === null,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password?: string) => {
    setLoading(true);
    try {
      if (password) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/budget`,
        },
      });
      if (error) throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/budget`,
        },
      });
      if (error) throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear backend user tracking when signing out
      setBackendUserCreated(new Set());
      localStorage.removeItem("pendingUsername");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateTokens = async (newTokens: number) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { tokens: newTokens },
      });
      if (error) throw error;
      setUser((prevUser) =>
        prevUser ? { ...prevUser, tokens: newTokens } : null,
      );
    } finally {
      setLoading(false);
    }
  };

  const signInAnonymously = async () => {
    setLoading(true);
    try {
      console.log(
        `signInAnonymously called, pendingUsername before: "${localStorage.getItem("pendingUsername")}"`,
      );
      // If we already have a session we don't need to create another one
      const {
        data: { session: current },
      } = await supabase.auth.getSession();
      if (!current) {
        const { error } = await supabase.auth.signInAnonymously();
        if (error) throw error;
      }
      console.log(
        `signInAnonymously completed, pendingUsername after: "${localStorage.getItem("pendingUsername")}"`,
      );
    } finally {
      setLoading(false);
    }
  };

  const linkAnonymousWithEmailPassword = async (
    email: string,
    password: string,
  ): Promise<"complete" | "verify_email"> => {
    setLoading(true);
    try {
      // Step 1. Attach the email to the current anonymous user.
      let { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;

      // Depending on your Auth configuration, the user might need to confirm
      // the email before we can set a password. Attempt to set the password –
      // if Supabase returns an error that the email must be verified first we
      // surface that to the caller so they can show an instructional message.
      const { error: pwdErr } = await supabase.auth.updateUser({ password });
      if (pwdErr) {
        // Supabase returns 422 "Updating password of an anonymous user..." until
        // the email identity is verified. Treat *that* specific error as
        // "verify_email" but surface all other validation errors (e.g. weak
        // password) back to the caller.
        if (
          pwdErr.status === 422 &&
          pwdErr.message.toLowerCase().includes("anonymous user")
        ) {
          return "verify_email";
        }
        throw pwdErr;
      }
      return "complete";
    } finally {
      setLoading(false);
    }
  };

  const value = {
    supabase,
    session,
    user,
    signInWithEmail,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    loading,
    updateTokens,
    signInAnonymously,
    linkAnonymousWithEmailPassword,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */
export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error("useSupabase must be used within a SupabaseProvider");
  }
  return context;
};
